import styled from 'styled-components';

import MediaQuery from 'react-responsive';
import { default as DefaultGrid } from '#/components/UnifiedRfqs/components/Grid';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { getVenue } from '@paradigm/features/src/unified-rfqs/entities/products/venue';
import {
  getLongNameWithoutVenue,
  getProduct,
} from '@paradigm/features/src/products/domain';
import { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import { Grfq } from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';
import MarkPrice from '#/components/UnifiedRfqs/components/MarkPrice';
import { bigStone, darkerBlackPearl } from '@paradigm/design-system/src/colors';
import ColoredStrategyDescription from '#/components/UnifiedRfqs/components/strategy-description/ColoredStrategyDescription';
import { Maybe } from '@paradigm/utils/types';

interface RfqStrategySummaryArgs {
  rfq: Drfq | Grfq;
  handleMarkPriceClick?: (price: string) => void;
}

export default function RfqStrategySummary({
  rfq,
  handleMarkPriceClick,
}: RfqStrategySummaryArgs) {
  return (
    <Container>
      <MediaQuery minWidth={MOBILE_BREAKPOINT}>
        <p css="text-align: left; margin: 0 0 .5rem;">Strategy</p>
      </MediaQuery>
      <Grid>
        <MediaQuery maxWidth={MOBILE_BREAKPOINT}>
          <ProductInfoContainer>
            <ProductInfo venue={rfq.venue} productCode={rfq.product_codes[0]} />
          </ProductInfoContainer>
        </MediaQuery>
        <Strategy data-testid="strategy-description">
          <ColoredStrategyDescription className="strategy-description">
            {rfq.description}
          </ColoredStrategyDescription>
        </Strategy>
        <MediaQuery minWidth={MOBILE_BREAKPOINT}>
          <MarkPrice handleMarkPriceClick={handleMarkPriceClick} />
        </MediaQuery>
      </Grid>
    </Container>
  );
}

export function ProductInfo(props: {
  venue: string;
  productCode: Maybe<string>;
}) {
  if (props.productCode == null) return null;
  const venue = getVenue(props.venue);
  const product = getProduct(props.productCode);
  const longName = getLongNameWithoutVenue(product.longName);
  return (
    <div data-testid="product-info">
      <p aria-label="product">{longName}</p>
      <p aria-label="venue">{venue.name}</p>
    </div>
  );
}

const ProductInfoContainer = styled.div`
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    p {
      margin: 0;
      opacity: 0.6;
      color: white;
      font-size: 0.875rem;
      font-weight: normal;
      line-height: 1.375rem;
    }
  }
`;

const Grid = styled(DefaultGrid)`
  grid-template-columns: 1fr 24ch;
  align-items: center;
  gap: 10px;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    align-items: flex-start;
    grid-template-columns: auto 1fr;
    gap: 1rem;
  }
`;

const Strategy = styled.div`
  padding: 10px 15px;
  border-radius: 5px;
  background-color: black;
  font-weight: bold;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    background: none;
    color: white;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
    text-align: right;
    padding: 0;
    border-radius: 0;
    margin: 0;
  }
`;

const Container = styled.div`
  margin: auto;

  .strategy-description {
    font-size: 0.75rem;
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    background: ${darkerBlackPearl};
    box-shadow: inset 0 -1px 0 0 ${bigStone};
    margin: -1rem;
    padding: 1rem;

    .strategy-description {
      font-size: 1rem;
      display: block;
    }
  }
`;
